import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BarChart3, Bell, CreditCard, Flag, FolderGit2, LayoutDashboard, LineChart, Package, Shield, Sparkles, Users, Video } from 'lucide-react';
import HeroDashboard from './HeroDashboard';

export const DASHBOARD_FEATURES = [
  { icon: LayoutDashboard, title: 'Stages you can track', body: 'Scope, design, build, QA, launch. Each stage has a percentage, an owner and a date, updated as we work.' },
  { icon: Package, title: 'Deliverables with status', body: 'Every artifact listed as delivered, in review, building or pending, so nothing is "almost done".' },
  { icon: Flag, title: 'Launch checklist', body: 'Staging, production, domain and SSL, monitoring, backups, handoff. Request launch when it is green.' },
  { icon: BarChart3, title: 'Sprint velocity', body: 'Planned versus delivered every week, on a chart, not in a status email.' },
  { icon: Video, title: 'Private meeting room', body: 'A Nighthouse Plus room replaces Zoom. Recordings and notes land next to the milestone they belong to.' },
  { icon: FolderGit2, title: 'Repo, staging and production', body: 'Links to your code, the weekly staging release and the live product. You own all three from day one.' },
  { icon: Bell, title: 'Updates feed', body: 'Progress notes, decisions and files in one thread, with an email ping when something needs you.' },
  { icon: CreditCard, title: 'Payments and invoices', body: 'Half to start, half when it ships. Deposits, balances and subscriptions tracked in the same place.' },
  { icon: Users, title: 'Team and roles', body: 'Invite co-founders or staff. Admins approve, clients see their projects, nobody sees anyone else\'s.' },
  { icon: LineChart, title: 'Analytics and conversions', body: 'GA4, conversion events and weekly reports wired into your site, visible in the hub once you launch.' },
  { icon: Sparkles, title: 'Product panels', body: 'Postify, FillAI and ValidMy.email panels switch on for clients, with the discounts that come with them.' },
  { icon: Shield, title: 'Auth built in', body: 'Sessions, refresh tokens with reuse detection, role-based access and rate limits. The same stack we ship to you.' },
];

// The features inside the client hub, listed once and shared by the home
// page and /demos. `action` lets each page choose where the list leads.
export default function DashboardFeatures({ title = 'What is inside the dashboard', lede, action, limit, className = '', showDashboard = false }) {
  const items = limit ? DASHBOARD_FEATURES.slice(0, limit) : DASHBOARD_FEATURES;
  return (
    <section className={'border-t border-line py-16 md:py-24 ' + className}>
      <div className="container-x">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <h2 className="reveal display-lg">{title}</h2>
            {lede ? <p className="reveal lede mt-6" data-delay="80">{lede}</p> : null}
          </div>
          {action ? <div className="reveal shrink-0" data-delay="120">{action}</div> : null}
        </div>
        {showDashboard ? (
          <div className="reveal mx-auto mt-10 w-full max-w-[640px]">
            <HeroDashboard />
            <p className="mt-3 text-center text-xs text-dim">The client hub every project gets.</p>
          </div>
        ) : null}
        <ul className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {items.map(({ icon: Icon, title: t, body }) => (
            <li key={t} className="reveal bg-ink p-6 md:p-7">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-cobalt/20 text-sky"><Icon className="h-4 w-4" /></span>
              <h3 className="mt-4 font-display text-xl font-medium tracking-tight">{t}</h3>
              <p className="mt-2 text-sm text-mist">{body}</p>
            </li>
          ))}
        </ul>
        {limit && limit < DASHBOARD_FEATURES.length ? (
          <Link to="/demos" className="mt-6 inline-flex items-center gap-1 text-sky hover:text-cream">All {DASHBOARD_FEATURES.length} features and the live mock <ArrowUpRight className="h-4 w-4" /></Link>
        ) : null}
      </div>
    </section>
  );
}
